/**
 * 路由守卫组件
 * 保护需要认证的路由
 */

import { Navigate, useLocation } from "react-router-dom"
import { useAuthStore } from "@/shared/store/authStore"
import { TokenManager } from "@/shared/services/api"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
}

/**
 * 路由保护组件
 * 需要登录才能访问的页面使用此组件包裹
 */
export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(true)

  // 页面刷新后，检查localStorage中的token，恢复认证状态
  useEffect(() => {
    const token = TokenManager.getToken()
    if (token && !isAuthenticated) {
      // 如果localStorage中有token但store中没有认证状态，恢复认证状态
      // 注意：这里只是恢复isAuthenticated状态，user信息需要从API获取
      useAuthStore.setState({ isAuthenticated: true })
    }
    setIsChecking(false)
  }, [isAuthenticated])

  // 加载中状态
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">加载中...</p>
        </div>
      </div>
    )
  }

  // 检查token是否存在（即使isAuthenticated为false，如果token存在也允许访问）
  const token = TokenManager.getToken()
  if (!token && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // 检查角色权限
  if (requiredRoles && requiredRoles.length > 0 && user) {
    if (!requiredRoles.includes(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-2">
              权限不足
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              您没有访问此页面的权限
            </p>
          </div>
        </div>
      )
    }
  }

  // 已登录（有token或isAuthenticated为true），渲染子组件
  return <>{children}</>
}

